import { createFileRoute, Link } from '@tanstack/react-router'
import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { css } from '../../../../styled-system/css'
import {
  useCancelNotificationByPersonalInformation,
  useGetNotificationByPersonalInformationHistories
} from '../../../libs/admin/hooks'
import type { NotificationByPersonalInformationHistory } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { useState } from 'react'
import type { Timestamp } from '@bufbuild/protobuf'

import Add from '~icons/material-symbols/add.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import { button } from '../../../styles/buttonStyles'
import { table, tableHead, tableHeadCell, tableRow, tableCell, tableWrapper } from '../../../styles/tableStyles'
import { pageTitle, titleWrapper } from '../../../styles/sectionStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { formatTimestampJaJst } from '../../../libs/utils/format-timestamp'
import { getNotificationSendingTimestamp } from './-get-notification-sending-timestamp'
import { dialogBackdrop } from '../../../styles/dialogStyles'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../components/table-pagination-toolbar'

export const Route = createFileRoute('/admin/notifications/')({
  component: RouteComponent,
})

const ITEMS_PER_PAGE = 20

const formatDate = (timestamp: Timestamp | undefined) => {
  if (!timestamp) return '-'
  return formatTimestampJaJst(timestamp, { withTime: true })
}

const isFutureScheduled = (timestamp: { seconds: bigint, nanos: number } | undefined) => {
  if (!timestamp) return false
  const scheduledMillis = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1e6)
  return scheduledMillis > Date.now()
}

const TableContent = ({
  notifications,
  isLoading,
  error,
  currentPage
}: {
  notifications: NotificationByPersonalInformationHistory[] | undefined
  isLoading: boolean
  error: Error | null
  currentPage: number
}) => {
  const cancelNotification = useCancelNotificationByPersonalInformation()
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<NotificationByPersonalInformationHistory | null>(null)

  const handleCancelScheduled = async () => {
    if (!cancelTarget?.scheduledAt) return
    try {
      setCancellingId(cancelTarget.id)
      await cancelNotification.mutateAsync({
        notificationByPersonalInformationHistoryId: cancelTarget.id
      })
    } catch (error) {
      console.error('Failed to cancel scheduled notification', error)
    } finally {
      setCancellingId(null)
      setIsCancelDialogOpen(false)
      setCancelTarget(null)
    }
  }

  const handleOpenCancelDialog = (notification: NotificationByPersonalInformationHistory) => {
    if (!isFutureScheduled(notification.scheduledAt)) return
    setCancelTarget(notification)
    setIsCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    if (cancellingId) return
    setIsCancelDialogOpen(false)
    setCancelTarget(null)
  }

  const isCancelling = Boolean(cancelTarget && cancellingId === cancelTarget.id)
  const shouldShowCancelDialog = Boolean(isCancelDialogOpen && cancelTarget)

  const renderCancelDialog = () => {
    if (!shouldShowCancelDialog) return null

    return (
      <Dialog
        open
        onClose={handleCloseCancelDialog}
        className={dialogBackdrop()}
      >
        <DialogPanel className={css({
          backgroundColor: 'white',
          width: '320px',
          margin: 'auto',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <DialogTitle className={css({
            fontSize: '16px',
            fontWeight: '600',
          })}>
            送信予約を取消しますか？
          </DialogTitle>
          <div className={css({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          })}>
            <button
              type='button'
              className={button({ color: 'ghost' })}
              onClick={handleCloseCancelDialog}
              disabled={isCancelling}
            >
              キャンセル
            </button>
            <button
              type='button'
              className={button()}
              onClick={handleCancelScheduled}
              disabled={isCancelling}
            >
              送信予約取消
              {isCancelling && <LoadingOverlay size='sm' />}
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    )
  }

  if (isLoading) {
    return (
      <>
        {renderCancelDialog()}
        <div className={css({ padding: '40px', textAlign: 'center' })}>
          読み込み中...
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        {renderCancelDialog()}
        <div className={css({ padding: '40px', textAlign: 'center', color: 'error' })}>
          エラーが発生しました: {error.message}
        </div>
      </>
    )
  }

  if (!notifications || notifications.length === 0) {
    return (
      <>
        {renderCancelDialog()}
        <div className={css({ padding: '40px', textAlign: 'center' })}>
          通知履歴がありません
        </div>
      </>
    )
  }

  const notificationsWithSendingDate = notifications.map((n) => ({
    notification: n,
    sendingDate: getNotificationSendingTimestamp(n),
  })).sort((a, b) => {
    const aMillis = a.sendingDate ? Number(a.sendingDate.seconds) * 1000 + Math.floor(a.sendingDate.nanos / 1e6) : 0
    const bMillis = b.sendingDate ? Number(b.sendingDate.seconds) * 1000 + Math.floor(b.sendingDate.nanos / 1e6) : 0
    return bMillis - aMillis
  })

  return (
    <>
      {renderCancelDialog()}
      <table className={table()}>
        <thead className={tableHead()}>
          <tr>
            <th className={tableHeadCell()}>作成日時</th>
            <th className={tableHeadCell()}>送信日時</th>
            <th className={tableHeadCell()}>タイトル</th>
            <th className={tableHeadCell()}>内容</th>
            <th className={tableHeadCell()}>URL</th>
            <th className={tableHeadCell()} />
          </tr>
        </thead>
        <tbody>
          {notificationsWithSendingDate
            .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
            .map(({ notification, sendingDate }) => (
              <tr key={notification.id} className={tableRow()}>
                <td className={tableCell()}>{formatDate(notification.createdAt)}</td>
                <td className={tableCell()}>{formatDate(sendingDate)}</td>
                <td className={tableCell()}>{notification.original?.titleTemplate}</td>
                <td className={tableCell()}>{notification.original?.messageTemplate}</td>
                <td className={tableCell()}>
                  <div className={css({
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  })}>
                    {notification.original?.serviceUrlTemplate}
                  </div>
                </td>
                <td className={tableCell()}>
                  <div className={css({
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  })}>
                    {isFutureScheduled(notification.scheduledAt) && (
                      <button
                        type='button'
                        className={button()}
                        onClick={() => handleOpenCancelDialog(notification)}
                        disabled={cancellingId === notification.id}
                      >
                        送信予約取消
                      </button>
                    )}
                    <Link to='/admin/notifications/$id' params={{ id: notification.id }}>
                      <button type='button' className={button()}>
                        詳細<ArrowForward />
                      </button>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  )
}

const Table = ({
  notifications,
  isLoading,
  error,
  currentPage,
  onPageChange
}: {
  notifications: NotificationByPersonalInformationHistory[] | undefined
  isLoading: boolean
  error: Error | null
  currentPage: number
  onPageChange: (page: number) => void
}) => {
  return (
    <div className={tableWrapper()}>
      <TablePaginationToolbar
        page={currentPage - 1}
        totalPages={Math.ceil((notifications?.length || 0) / ITEMS_PER_PAGE)}
        onPageChange={(page) => onPageChange(page + 1)}
      />
      <TableContent
        notifications={notifications}
        isLoading={isLoading}
        error={error}
        currentPage={currentPage}
      />
    </div>
  )
}

function RouteComponent() {
  const { data: notifications, isLoading, error } = useGetNotificationByPersonalInformationHistories()
  const [currentPage, setCurrentPage] = useState(1)

  // Reset to page 1 when data changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>プッシュ通知</h1>
        <div className={css({
          display: 'flex',
          flexGrow: 1,
        })} />
        <Link to="/admin/notifications/new">
          <button type="button" className={button({ color: 'primary' })}>
            <Add />
            プッシュ通知作成
          </button>
        </Link>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <Table
          notifications={notifications}
          isLoading={isLoading}
          error={error}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  )
}
